import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import LatestBadge from './LatestBadge';
import { getSortedGalleryItems } from '../../assets/data/gallery';

const CarouselView = ({ images, categoryColor, onImageClick }) => {
  // Sort images with latest first
  const sortedImages = getSortedGalleryItems(images);
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sortedImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sortedImages.length) % sortedImages.length);
  };

  // Auto-advance the carousel
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const renderMediaContent = (item, index) => {
    if (item.type === 'video') {
      return (
        <div style={styles.videoContainer}>
          {/* Video thumbnail with play button overlay */}
          <img 
            src={item.thumbnail} 
            alt={item.alt} 
            style={styles.slideImage} 
          />
          <div style={styles.playOverlay}>
            <div style={styles.playButton}>
              <i className="fas fa-play" style={styles.playIcon}></i>
            </div>
          </div>
          <div style={styles.videoLabel}>
            <i className="fas fa-video" style={styles.videoIcon}></i>
            <span>Video</span>
          </div>
        </div>
      );
    } else {
      return (
        <img src={item.src} alt={item.alt} style={styles.slideImage} />
      );
    }
  };

  return (
    <motion.div 
      style={styles.carousel}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div style={styles.container}>
        <button style={styles.buttonPrev} onClick={prevSlide}>
          <i className="fa fa-chevron-left"></i>
        </button>
        
        <div style={styles.track}>
          {sortedImages.map((item, index) => (
            <motion.div 
              key={item.src || item.id} 
              style={{
                ...styles.slide,
                opacity: currentSlide === index ? 1 : 0,
                pointerEvents: currentSlide === index ? 'all' : 'none'
              }}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: currentSlide === index ? 1 : 0, x: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => onImageClick(item)}
            >
              {/* Show latest badge if this is the latest installation */}
              {item.isLatest && <LatestBadge />}
              {renderMediaContent(item, index)}
              <div style={styles.caption}>
                <h3 style={styles.captionTitle}>{item.alt}</h3>
                <p style={styles.captionText}>{item.caption}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        <button style={styles.buttonNext} onClick={nextSlide}>
          <i className="fa fa-chevron-right"></i>
        </button>
      </div>
      
      <div style={styles.indicators}>
        {sortedImages.map((_, index) => (
          <button 
            key={index} 
            style={{
              ...styles.indicator,
              backgroundColor: currentSlide === index ? categoryColor : '#ccc'
            }}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </motion.div>
  );
};

const styles = {
  carousel: {
    position: 'relative',
    marginBottom: '60px',
  },
  container: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '8px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
    height: '500px',
  },
  track: {
    position: 'relative',
    height: '100%',
  },
  slide: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: 'opacity 0.5s ease',
    cursor: 'pointer',
  },
  slideImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  videoContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  playOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
  },
  playButton: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    border: '3px solid rgba(255, 255, 255, 0.8)',
  },
  playIcon: {
    fontSize: '24px',
    color: 'white',
    marginLeft: '4px', // Slight offset for visual centering
  },
  videoLabel: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '15px',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    zIndex: 2,
  },
  videoIcon: {
    fontSize: '0.8rem',
  },
  caption: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '20px',
    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0))',
    color: 'white',
  },
  captionTitle: {
    margin: '0 0 5px',
    fontSize: '1.4rem',
  },
  captionText: {
    margin: 0,
    fontSize: '1rem',
    opacity: 0.9,
  },
  buttonPrev: {
    position: 'absolute',
    top: '50%',
    left: '15px',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    color: '#0078ff',
    cursor: 'pointer',
    zIndex: 2,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
  },
  buttonNext: {
    position: 'absolute',
    top: '50%',
    right: '15px',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    color: '#0078ff',
    cursor: 'pointer',
    zIndex: 2,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
  },
  indicators: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '15px',
  },
  indicator: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: 'none',
    margin: '0 5px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
};

export default CarouselView;